import type { FacilityType, Frequency } from './types';

/**
 * The commercial rate card.
 *
 * Single source of truth for every price shown anywhere on the site — the /pricing
 * page, the estimate widget, and the capability-statement PDF all read from here.
 * Change a number once and every surface follows.
 *
 * Base provenance: reconciled across three independent research passes (researcher
 * agent, Codex, Perplexity) and recorded in memory `core-cleans-commercial-pricing`.
 *
 * ⚠ 2026-09-01: the 3x/5x tiers below are bumped +$0.01/sqft on office, medical, and
 * industrial, at Vikelas's deliberate request, from a 2026-08-27 "profit
 * optimization" email. That email is verbatim, unsourced AI-chatbot output — its
 * "Tampa market average" claims are NOT verified (a separate sweep found 0 of 17 Tampa
 * commercial cleaners publish pricing at all), and its suggested range was $0.01–0.03.
 * $0.01 was chosen, not the range midpoint, because anything larger collides with the
 * weekly rate and breaks the "frequency, not quality" story — a tier must always stay
 * strictly cheaper than the one above it (enforced by pricing.test.ts). Retail was left
 * untouched; that email didn't mention it. The reconciled pre-bump numbers remain in
 * memory `core-cleans-commercial-pricing` for reference.
 */

/** Dollars per square foot, per visit. */
export const RATE_CARD: Record<FacilityType, Record<Frequency, number> | null> = {
  office: { biweekly: 0.2, weekly: 0.16, '3x': 0.15, '5x': 0.13 },
  retail: { biweekly: 0.18, weekly: 0.14, '3x': 0.13, '5x': 0.11 },
  medical: { biweekly: 0.26, weekly: 0.22, '3x': 0.2, '5x': 0.18 },
  industrial: { biweekly: 0.22, weekly: 0.18, '3x': 0.16, '5x': 0.14 },
  // "Other" has no published rate — it goes to a walkthrough, not a calculator.
  other: null,
};

/** No visit is quoted below this, however small the space. */
export const VISIT_MINIMUM = 175;

/** One-off work, priced per square foot, quoted separately from a recurring contract. */
export const PROJECT_RATES = {
  postConstruction: {
    rate: 0.4,
    label: 'Post-construction final clean',
    note: 'Vacant space, normal construction debris. Hauling, exterior work, and repeat phases are bid separately.',
  },
  stripAndWax: {
    rate: 0.8,
    label: 'Floor strip & wax',
    note: 'Starting rate. Final price depends on floor condition and the number of coats.',
  },
  medicalSanitationPlus: {
    rate: 0.29,
    label: 'Medical sanitation-plus',
    note: 'Enhanced disinfection protocol for medical and dental spaces, billed per visit alongside the base recurring rate.',
  },
} as const;

export const FACILITY_LABELS: Record<FacilityType, string> = {
  office: 'Office',
  retail: 'Retail',
  medical: 'Medical & dental',
  industrial: 'Warehouse & industrial',
  other: 'Something else',
};

/**
 * Frequency tiers — never quality tiers.
 *
 * Facility managers buy scope and frequency. "Basic / Standard / Premium" reads to
 * them as "Basic = we cut corners," which is exactly the fear the site exists to
 * remove. `visitsPerMonth` uses 4.333 weeks per month so the monthly figure is honest
 * rather than rounded down to a number we would then have to walk back.
 */
export const FREQUENCY_TIERS: {
  key: Frequency;
  label: string;
  cadence: string;
  visitsPerMonth: number;
  featured?: boolean;
}[] = [
  { key: '5x', label: 'Nightly', cadence: '5 nights a week', visitsPerMonth: 21.67 },
  { key: '3x', label: '3× a week', cadence: 'Three nights a week', visitsPerMonth: 13 , featured: true },
  { key: 'weekly', label: 'Weekly', cadence: 'Once a week', visitsPerMonth: 4.333 },
  { key: 'biweekly', label: 'Every other week', cadence: 'Twice a month', visitsPerMonth: 2.167 },
];

/**
 * Florida sales tax.
 *
 * Cleaning a NONRESIDENTIAL interior is a taxable service in Florida: 6% state plus the
 * county discretionary surtax (DOR GT-800015 / Rule 12A-1.0091). Residential cleaning is
 * exempt — which is why New Leaf never had to deal with this and this company does.
 *
 * Rates are per county and change by county referendum, so they live here as data with
 * an `asOf` date rather than being baked into a template.
 */
export const SALES_TAX = {
  asOf: '2026-08-28',
  counties: [
    { county: 'Hillsborough', rate: 0.075 },
    { county: 'Pinellas', rate: 0.07 },
    { county: 'Pasco', rate: 0.07 },
    { county: 'Polk', rate: 0.07 },
  ],
} as const;

export interface Estimate {
  /** Per-visit price, before sales tax, after the minimum is applied. */
  perVisit: number;
  /** Per-visit price the raw rate produced, before the minimum. */
  rawPerVisit: number;
  /** True when the visit minimum set the price rather than the square footage. */
  minimumApplied: boolean;
  perMonth: number;
  ratePerSqft: number;
}

/**
 * Estimate one visit.
 *
 * Returns null for facility types with no published rate — those go to a walkthrough
 * rather than being guessed at.
 */
export function estimateVisit(
  facilityType: FacilityType,
  frequency: Frequency,
  sqft: number,
): Estimate | null {
  const rates = RATE_CARD[facilityType];
  if (!rates || !Number.isFinite(sqft) || sqft <= 0) return null;

  const ratePerSqft = rates[frequency];
  const rawPerVisit = sqft * ratePerSqft;
  const perVisit = Math.max(rawPerVisit, VISIT_MINIMUM);
  const tier = FREQUENCY_TIERS.find((t) => t.key === frequency);

  return {
    perVisit,
    rawPerVisit,
    minimumApplied: rawPerVisit < VISIT_MINIMUM,
    perMonth: perVisit * (tier?.visitsPerMonth ?? 0),
    ratePerSqft,
  };
}

/** What every recurring visit includes, at every frequency. No upsell tiers. */
export const ALWAYS_INCLUDED = [
  'Restrooms cleaned, sanitised, and dispensers restocked',
  'All trash collected and liners replaced',
  'High-touch points disinfected — door handles, switches, rails, shared desks',
  'Floors vacuumed, swept, and mopped',
  'Break room and kitchen surfaces, sinks, and appliance fronts',
  'Glass doors and interior partition glass spot-cleaned',
  'Lights off, doors locked, alarm set — and signed off every visit',
] as const;

/** Priced separately, on request. Named plainly so nothing feels like a hidden fee. */
export const PRICED_SEPARATELY = [
  'Floor strip and wax, or burnishing',
  'Carpet extraction and spot treatment',
  'Interior and exterior window washing',
  'Disinfection fogging',
  'Day porter coverage during business hours',
  'Post-construction final cleans',
  'Pressure washing',
] as const;
